import { ArrowUpRight } from "lucide-react";
import { Link } from "wouter";
import PageMeta from "@/components/PageMeta";
import SiteFooter from "@/components/SiteFooter";
import SiteHeader from "@/components/SiteHeader";

const brandPrinciples = [
  {
    number: "01",
    title: "Why DuraTarp?",
    text: "We bring together product knowledge, application thinking and practical sourcing support. Our product and material routes are organized to help buyers understand the available options and identify the specifications that matter for their projects.",
  },
  {
    number: "02",
    title: "What We Believe",
    text: "We believe reliable business is built on accurate information and consistent execution. Technical claims should be supported by the relevant product specification, technical data sheet, test report or order-specific documentation. When a requirement depends on the exact construction or formulation, we say so clearly.",
  },
  {
    number: "03",
    title: "How We Work",
    text: "We help importers, distributors, manufacturers, fabricators and project buyers evaluate PVC technical fabrics according to application, construction and required performance. For standard products, the published SKU information provides the reference point. For project requirements, we support an order-specific discussion.",
  },
  {
    number: "04",
    title: "Who We Serve",
    text: "Our materials are used across transport covers, truck and trailer systems, tents and shelters, industrial protection, awnings, architectural structures, inflatable products and other outdoor or engineered applications.",
  },
];

export default function AboutDuraTarpPage() {
  return (
    <div className="min-h-screen bg-[#f5f5f3] text-[#171717]">
      <PageMeta
        title="About DuraTarp | PVC Tarpaulin & Coated Technical Fabric"
        description="Learn how DuraTarp helps professional buyers select PVC tarpaulin and coated technical fabric by application, construction, specification and performance requirements."
      />
      <SiteHeader />
      <main>
        <section className="relative isolate overflow-hidden border-b-4 border-[#a82020] bg-[#f5f1e9] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
          <div className="absolute inset-0 industrial-grid opacity-30" />
          <div className="absolute -right-20 top-0 h-full w-[46%] origin-top-right -skew-x-[28deg] border-l border-[#e44a43]/40 bg-[#a82020]/10" />
          <div className="absolute left-0 top-0 h-2 w-[28%] bg-[#a82020]" />
          <div className="relative mx-auto max-w-[1344px]">
            <p className="eyebrow flex items-center gap-3 text-[#171717]/60"><span className="h-px w-10 bg-[#e44a43]" />About DuraTarp</p>
            <span className="material-stamp mt-6 text-[#171717]/60">Brand / technical fabric sourcing</span>
            <h1 className="mt-7 max-w-4xl font-display text-[clamp(3.8rem,8vw,8rem)] uppercase leading-[.8] tracking-[-.02em]">Know materials.<br /><span className="text-[#e44a43]">Build trust.</span></h1>
            <p className="mt-7 max-w-2xl text-base leading-7 text-[#171717]/70 sm:text-lg">DuraTarp is a PVC tarpaulin and coated technical fabric brand serving professional buyers across demanding industrial and commercial applications.</p>
          </div>
        </section>

        <section className="px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
          <div className="mx-auto grid max-w-[1344px] gap-12 lg:grid-cols-[.78fr_1.22fr]">
            <div>
              <p className="eyebrow text-[#7a1717]">The DuraTarp approach</p>
              <h2 className="mt-4 font-display text-6xl uppercase leading-[.8]">Start with<br />the application.</h2>
            </div>
            <p className="max-w-3xl text-lg leading-8 text-[#625c55]">We believe sourcing technical fabric should be clearer than it often is. Different constructions, base fabrics, GSM levels, widths, surfaces and performance requirements can lead to very different results in the field. That is why our approach starts with the application, not simply the product name.</p>
          </div>
        </section>

        <section className="bg-[#171717] px-5 py-20 text-[#f5f1e9] sm:px-8 lg:px-12 lg:py-28">
          <div className="mx-auto max-w-[1344px]">
            <p className="eyebrow text-[#e44a43]">Brand principles</p>
            <div className="mt-8 grid gap-px bg-white/15 sm:grid-cols-2">
              {brandPrinciples.map((principle) => (
                <article key={principle.number} className="min-h-[270px] bg-[#171717] p-6 sm:p-8">
                  <span className="font-display text-4xl text-[#e44a43]">{principle.number}</span>
                  <h2 className="mt-10 text-base font-extrabold">{principle.title}</h2>
                  <p className="mt-3 max-w-xl text-sm leading-6 text-[#f5f1e9]/65">{principle.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f5f1e9] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
          <div className="mx-auto grid max-w-[1344px] gap-12 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
            <div>
              <p className="eyebrow text-[#7a1717]">Where we are going</p>
              <h2 className="mt-4 font-display text-6xl uppercase leading-[.8]">A dependable<br /><span className="text-[#e44a43]">technical partner.</span></h2>
            </div>
            <div>
              <p className="max-w-3xl text-lg leading-8 text-[#625c55]">DuraTarp is being built as a long-term international brand. Our ambition is not simply to supply more fabric. It is to become a dependable technical fabric partner that professional buyers can return to when material selection, supply consistency and application performance matter.</p>
              <p className="mt-8 font-display text-3xl uppercase leading-[.9] text-[#7a1717]">Know Materials.<br />Build Trust.<br />Deliver Performance.</p>
              <nav aria-label="Continue exploring DuraTarp" className="mt-10 flex flex-wrap gap-x-6 gap-y-4">
                <Link href="/products" className="inline-flex items-center gap-2 border-b border-[#e44a43] pb-1 text-[.66rem] font-extrabold uppercase tracking-[.14em]">Browse products <ArrowUpRight className="h-3.5 w-3.5" /></Link>
                <Link href="/applications" className="inline-flex items-center gap-2 border-b border-[#e44a43] pb-1 text-[.66rem] font-extrabold uppercase tracking-[.14em]">Explore applications <ArrowUpRight className="h-3.5 w-3.5" /></Link>
                <Link href="/materials" className="inline-flex items-center gap-2 border-b border-[#e44a43] pb-1 text-[.66rem] font-extrabold uppercase tracking-[.14em]">Compare materials <ArrowUpRight className="h-3.5 w-3.5" /></Link>
                <Link href="/contact" className="inline-flex items-center gap-2 border-b border-[#e44a43] pb-1 text-[.66rem] font-extrabold uppercase tracking-[.14em]">Start a discussion <ArrowUpRight className="h-3.5 w-3.5" /></Link>
              </nav>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
